import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Alert, Button, Card, Checkbox, Flex, Modal, Select, Space, Typography } from "antd"
import { gold } from "@ant-design/colors"
import LabeledSwitch from "../../common/LabeledSwitch"
import VSpace from "../../common/VSpace"
import { canCopyProposalDescription, canIncludeItemsSummary, InvoiceWorkareaGroup, shouldShowTaxRateSelector } from "./createInvoiceModel"
import { Action } from "./createInvoiceReducer"
import DollarInput from "../../common/DollarInput"
import { PricingTypes, pricingTypeName } from "../../../common/constants"
import { centsToDollarsDisplay } from "../../displayFormatters"
import RichTextEditor from "../../RichTextEditor/RichTextEditor"
import ActualItemSelector from "./ActualItemSelector"
import { useContext, useState } from "react"
import { AuthenticationContext } from "../../../AuthenticationContext"
import { hasValue } from "../../../helpers/nullable"
import InvoiceGroupWorkareasTable from "../InvoiceGroupWorkareasTable"
import VisitSelector from "./VisitSelector"
import { useGetTaxCodesQuery } from "../../../domain/invoicing/invoicingQueries"
import { InvoicingSystem } from "../../../domain/invoicing/settings/invoicingSettings"
import TaxRateSelector from "../TaxRates/TaxRateSelector"

const { Text } = Typography

type Props = {
  isPricingPending?: boolean
  isPricingError?: boolean
  group: InvoiceWorkareaGroup
  isDownPayment: boolean
  dispatch: (action: Action) => void
  invoicingSystem: InvoicingSystem
  jobId: number
}

const InvoiceGroup = ({ group, isDownPayment, dispatch, invoicingSystem, jobId, isPricingPending, isPricingError }: Props) => {
  const [isWorkareasModalOpen, setIsWorkareasModalOpen] = useState(false)
  const { currentUser } = useContext(AuthenticationContext)
  const showTaxCode = currentUser?.tenantQuickbooksCompanyCountryCode !== 'US'
  const getTaxCodesQuery = useGetTaxCodesQuery(showTaxCode)
  const taxCodes = getTaxCodesQuery.data ?? []
  const showTaxRateSelector = shouldShowTaxRateSelector(invoicingSystem, group)

  const showPerWorkareaAmounts = group.pricingType === PricingTypes.QuotedPrice && !group.hidden

  const itemGroupingOptions = [
    { label: 'Line Item Per Workarea', value: 1 },
    { label: 'Line Item Per Workarea and Visit', value: 2 },
  ]
  if (group.allowLineItemPerWorkareaItem) {
    itemGroupingOptions.push({ label: 'Line Item Per Workarea Item', value: 3 })
  }
  if (group.allowLineItemPerGroup) {
    itemGroupingOptions.unshift({ label: 'Line Item Per Group', value: 4 })
  }

  const isQuotedOrPerVisitPricing = group.pricingType === PricingTypes.QuotedPrice || group.pricingType === PricingTypes.PerVisit

  return <Card>
    <VSpace>
      <Flex justify="space-between">
        <LabeledSwitch
          checked={group.selected}
          onChange={() => dispatch({ type: 'GROUP_SELECTED', groupId: group.id, value: !group.selected })}
          label={group.name}
        />
        {group.selected && <div style={{ fontSize: '1.5em' }}>
          {isPricingPending ? 'Calculating…' : isPricingError ? 'Unavailable' : centsToDollarsDisplay(group.amount)}
        </div>}
      </Flex>
      {group.selected &&
        <>
          {isQuotedOrPerVisitPricing && (!isDownPayment || showTaxCode) &&
            <Flex justify="space-between">
              <Checkbox
                checked={group.showTaxInFinalPrice}
                onChange={() => dispatch({ type: 'TAXABLE_CHANGED', groupId: group.id, value: !group.showTaxInFinalPrice })}
              >
                Taxable
              </Checkbox>
              {group.showTaxInFinalPrice && !isDownPayment &&
                <span style={{ color: gold[7] }}>
                  <Space>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    Sales tax gets calculated after the invoice is generated
                  </Space>
                </span>
              }
              {isDownPayment &&
                <span style={{ color: gold[7] }}>
                  <Space>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    <span>Learn more about how tax is collected <a href="https://help.synkedup.com/en/articles/4924198" target="_blank">here.</a></span>
                  </Space>
                </span>
              }
            </Flex>
          }
          {group.pricingType === PricingTypes.QuotedPrice && isDownPayment && !showTaxCode &&
            <span style={{ color: gold[7] }}>
              <Space>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>Sales tax is not collected on down payment invoices. <a href="https://help.synkedup.com/en/articles/4844573" target="_blank">Learn more.</a></span>
              </Space>
            </span>
          }
          {showTaxCode && hasValue(currentUser?.tenant.defaultQboSalesTaxId) && <VSpace>
            Sales Tax
            <Select
              value={group.selectedSalesTaxId}
              options={taxCodes?.map(x => ({ label: x.name, value: parseInt(x.id) })) ?? []}
              onChange={(value) => dispatch({ type: 'TAX_CODE_CHANGED', groupId: group.id, value })}
              popupMatchSelectWidth={false}
            />
          </VSpace>}
          {showTaxRateSelector && <VSpace>
            Tax Rate
            <TaxRateSelector
              invoicingSystem={invoicingSystem}
              jobId={jobId}
              selectedTaxRateId={group.taxRateId}
              canEditGroupFields={true}
              onChange={x => dispatch({ type: 'TAX_RATE_ID_CHANGED', groupId: group.id, value: x })}
            />
          </VSpace>}
          {showPerWorkareaAmounts &&
            <div style={{ textAlign: 'right' }}>
              <div>PricingType: {pricingTypeName(group.pricingType)}</div>
              <div>Billable Amount: {centsToDollarsDisplay(group.billableAmount)}</div>
            <div>Already Invoiced: {centsToDollarsDisplay(group.alreadyInvoiced)}</div>
          </div>}
          {showPerWorkareaAmounts &&
            <Flex>
              {group.workareas.length <= 5 ?
                <InvoiceGroupWorkareasTable
                  group={group}
                  pagination={false}
                  dispatch={dispatch}
                />
                :
                <Button
                  type="primary"
                  onClick={() => setIsWorkareasModalOpen(true)}
                >
                  Specify Work Area Amounts
                </Button>
              }
            </Flex>
          }
          {!showPerWorkareaAmounts && <Flex justify="space-between">
            {group.pricingType === PricingTypes.QuotedPrice &&
              <div>
                Amount
                <br />
                <Space>
                  <DollarInput
                    value={group.amount}
                    onChange={value => dispatch({ type: 'AMOUNT_CHANGED', groupId: group.id, value: value ?? 0 })}
                    min={0}
                    max={group.unbilledAmount}
                  />
                </Space>
              </div>
            }
            {group.pricingType !== PricingTypes.QuotedPrice &&
              <div>
                Group Items
                <br />
                <Select
                  options={itemGroupingOptions}
                  style={{ width: '300px' }}
                  value={group.itemGroupingOption}
                  onChange={(value) => dispatch({ type: 'ITEM_GROUPING_OPTION_CHANGED', groupId: group.id, value })}
                />
              </div>
            }
            <div style={{ textAlign: 'right' }}>
              <div>PricingType: {pricingTypeName(group.pricingType)}</div>
              <div>Billable Amount: {centsToDollarsDisplay(group.billableAmount)}</div>
              <div>Already Invoiced: {centsToDollarsDisplay(group.alreadyInvoiced)}</div>
            </div>
          </Flex>}
          {group.showTaxInFinalPrice && group.deductionAmount > 0 && <>
            <Checkbox
              checked={group.includeDeduction}
              onChange={() => dispatch({ type: 'INCLUDE_DEDUCTION_CHANGED', groupId: group.id, value: !group.includeDeduction })}
            >
              Collect sales tax for the {centsToDollarsDisplay(group.deductionAmount)} down-payment paid for this work area
            </Checkbox>
            {!group.includeDeduction && group.amount === group.unbilledAmount && <Alert
              message="Once fully invoiced, you will no longer be able to collect sales tax on the down-payment."
              type="warning"
              showIcon
            />}
          </>}
          <VSpace size="small">
            <Flex justify="space-between" align="center">
              <span>Invoice Description</span>
              <Button
                type="link"
                size="small"
                disabled={!canCopyProposalDescription(group)}
                onClick={() => dispatch({ type: 'PROPOSAL_DESCRIPTION_COPIED', groupId: group.id })}
              >
                Copy from proposal
              </Button>
            </Flex>
            <RichTextEditor
              value={group.description}
              onChange={(value) => dispatch({ type: 'DESCRIPTION_CHANGED', groupId: group.id, value })}
            />
            <Text type="secondary">Changes here apply to this invoice only. They don't change the proposal or the plan's invoice description.</Text>
          </VSpace>
          {canIncludeItemsSummary(group.pricingType) &&
            <Checkbox
              checked={group.includeItemsSummary}
              onChange={() => dispatch({ type: 'INCLUDE_ITEMS_SUMMARY_CHANGED', groupId: group.id, value: !group.includeItemsSummary })}
            >
              Include items summary in description
            </Checkbox>
          }
          {group.pricingType !== PricingTypes.QuotedPrice && group.pricingType !== PricingTypes.PerVisit &&
            <ActualItemSelector workareaGroup={group} invoiceDispatch={dispatch} />
          }
          {
            group.pricingType === PricingTypes.PerVisit &&
            <VisitSelector group={group} dispatch={dispatch} />
          }
        </>
      }
    </VSpace>
    <Modal
      title="Specify Work Area Amounts"
      open={isWorkareasModalOpen}
      cancelText="Close"
      onCancel={() => {
        setIsWorkareasModalOpen(false)
      }}
      okButtonProps={{
        hidden: true
      }}
      style={{ minWidth: '800px' }}
    >
      <VSpace>
        <InvoiceGroupWorkareasTable
          group={group}
          pagination={true}
          dispatch={dispatch}
        />
        <Flex justify="end">
          <strong>Total: {isPricingPending ? 'Calculating…' : isPricingError ? 'Unavailable' : centsToDollarsDisplay(group.amount)}</strong>
        </Flex>
      </VSpace>
    </Modal>
  </Card>
}

export default InvoiceGroup
